import { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, ButtonGroup, Modal, Tooltip, OverlayTrigger } from 'react-bootstrap';
import PageHeader from '../../components/PageHeader';
import Swal from 'sweetalert2';
import { apiUrl } from '../../config/apiUrls';
import { useNavigate } from 'react-router-dom';
import { generateSlug } from '../../config/generateDebateSlug';
import { useUserTopic } from '../../hooks/ai-hooks/useCommSkillsTopic';
import { v4 as uuidv4 } from 'uuid';
import { usePaymentStore } from '../../hooks/ai-hooks/usePaymentCredit';
import { Info } from 'lucide-react';

function DebatePrep() {
    const BASE_URL = process.env.REACT_APP_AI_API_URL
    const router = useNavigate()
    const [loader, setLoader] = useState(false)
    const [showHowItWorks, setShowHowItWorks] = useState(false);
    const studentId = sessionStorage.getItem("id")
    const [showTopicInfo, setShowTopicInfo] = useState(false);
    const [showDebatePreference, setShowDebatePreference] = useState(false);

    const [formData, setFormData] = useState({
        argueTopic: "",
        difficulty: "easy",
        argueType: true,
        debatePreference: "",
        duration: "10"
    });

    useEffect(() => {
        if (formData.argueTopic) {
            const topic = formData?.argueTopic?.toLowerCase();

            const hasVs =
                topic.includes(" vs ") ||
                topic.includes(" vs.") ||
                topic.includes(" verses ") ||
                topic.includes(" versus ");

            setShowDebatePreference(hasVs);

            // Clear preference if hidden
            if (!hasVs) {
                handleChange("debatePreference", "");
            }
        }
    }, [formData.argueTopic]);


    const setupChecklist = [
        {
            id: 1,
            title: "Microphone Access",
            desc: "Enable microphone permissions for voice interaction",
            icon: "bi-mic-fill",
            required: true
        },
        {
            id: 2,
            title: "Camera ( Optional )",
            desc: "Enable camera for non-verbal cues analysis",
            icon: "bi-camera-video-fill",
            required: false
        },
        {
            id: 3,
            title: "Stable Internet",
            desc: "Ensure 5+ Mbps connection for smooth experience",
            icon: "bi-wifi",
            required: true
        },
        {
            id: 4,
            title: "Quiet Environment",
            desc: "Choose a quiet space to minimize background noise",
            icon: "bi-volume-mute-fill",
            required: true
        },
        {
            id: 5,
            title: "Headphones ( Recommended )",
            desc: "Use headphones for better audio clarity",
            icon: "bi-headphones",
            required: false
        }
    ];

    const howItWorksSteps = [
        {
            step: "1",
            title: "Prepare Environment",
            desc: "Quiet space, stable internet, working microphone."
        },
        {
            step: "2",
            title: "Voice Interaction",
            desc: "AI will speak, respond via voice or text."
        },
        {
            step: "3",
            title: "Dynamic Role-Play",
            desc: "Engage in realistic scenarios like client calls, team meetings, or presentations"
        },
    ];

    const startInterview = async (payload) => {
        try {
            const response = await fetch(
                `${BASE_URL}${apiUrl.debatePrepStart}`,
                {
                    method: "POST",
                    body: JSON.stringify(payload),
                }
            );

            const result = await response.json();
            console.log("result", result)

            // SUCCESS
            if (response.status === 200) {
                if (result?.paymentData) {
                    usePaymentStore.getState().setPaymentData(result.paymentData);
                }

                router(`/practice-with-ai/debate/${result.uniqueId}`);
                return;
            }

            if (response.status === 402) {
                if (result?.paymentData) {
                    usePaymentStore.getState().setPaymentData(result.paymentData);
                }
                Swal.fire('Insufficient Credits', "You’ve used all credits. Upgrade your plan to continue practicing and improving 🚀", 'warning')

                return;
            }

            Swal.fire("Failed to Start", result?.message || "Please try again later", "error");

            // router(`/practice-with-ai/debate/${"uniqueId"}`);
        } catch (error) {
            console.error(error);
            Swal.fire("Failed to Start", "Network error. Please try again.", "error");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true)
        const uniqueId = uuidv4();
        const stance = formData.argueType == true ? "against" : "support";

        const payload = {
            duration: formData.duration,
            level: formData.difficulty,
            debateName: formData.argueTopic,
            argueType: stance,
            debatePreference: formData.debatePreference,
            userId: studentId,
            uniqueId,
            topic: generateSlug(formData.argueTopic, formData.difficulty, stance, formData.duration)
        };

        try {
            await startInterview({ ...payload, uniqueId });
            useUserTopic.getState().setModuleData("debate", payload);
        } catch (error) {
            console.error(error)
        } finally {
            setLoader(false)
        }
    }

    const handleChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };


    return (
        <>
            <Row>
                <Col>
                    <PageHeader
                        title="AI Debating skills. Preparation"
                        breadcrumbs={[
                            { label: "Dashboard", path: "/dashboard" },
                            { label: "Practice With AI", path: '/practice-with-ai' },
                            { label: "Debating skills.", active: true },
                        ]}
                    />
                </Col>
            </Row>

            <Container className="py-4">
                <Row className="g-4">
                    <Col lg={6} className="order-2 order-lg-1">
                        <Card className="border rounded-md mb-4">
                            <Card.Header className="bg-white border-0 pt-4 px-4 ">
                                <h5 className="fw-bold mb-1">
                                    <i className="bi bi-gear-fill text-primary me-2"></i>
                                    Setup Requirements
                                </h5>
                                <small className="text-muted">Ensure you meet these requirements before starting</small>
                            </Card.Header>
                            <Card.Body className="px-4 pb-4">
                                {setupChecklist.map((item, idx) => (
                                    <div
                                        key={item.id}
                                        className={`d-flex align-items-start p-3 rounded-3 ${idx !== setupChecklist.length - 1 ? 'mb-2' : ''}`}
                                        style={{ backgroundColor: '#f8f9fa' }}
                                    >
                                        <div className="me-3">
                                            <i className={`bi ${item.icon} fs-4 text-primary`}></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                                <h6 className="mb-0 fw-semibold">{item.title}</h6>
                                                {item.required && (
                                                    <Badge bg="danger" className="ms-2 px-2 py-1" style={{ fontSize: '0.65rem' }}>
                                                        Required
                                                    </Badge>
                                                )}
                                            </div>
                                            <small className="text-muted">{item.desc}</small>
                                        </div>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={6} className="order-1 order-lg-2">
                        <div className="d-flex align-items-center justify-content-center w-full">
                            <Card className="w-100" >
                                <Card.Body>
                                    <div className='text-center p-4 border-bottom'>
                                        <h5 className="fw-bold mb-1">
                                            <i className="ri-p2p-line text-primary fs-4 me-2"></i>Debating skills
                                        </h5>
                                        <p className='text-muted'>Strengthen your reasoning, improve communication.</p>
                                    </div>
                                    <Form onSubmit={handleSubmit} className="d-flex flex-column gap-3" style={{ marginTop: "17px" }}>
                                        <Form.Group>
                                            {/* <Form.Label className="fw-semibold">Debate Topic</Form.Label> */}
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <Form.Label className="fw-semibold mb-0">Debate Topic</Form.Label>

                                                <OverlayTrigger
                                                    placement="right"
                                                    overlay={<Tooltip>How to choose a perfect topic</Tooltip>}
                                                >
                                                    <Info
                                                        role="button"
                                                        size={16}
                                                        onClick={() => setShowTopicInfo(true)}
                                                    />
                                                </OverlayTrigger>
                                            </div>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter your debate topic"
                                                name='argueTopic'
                                                required
                                                isInvalid={showDebatePreference}
                                                value={formData.argueTopic}
                                                onChange={(e) => handleChange("argueTopic", e.target.value)}
                                            />
                                            {showDebatePreference && (
                                                <Form.Control.Feedback type="invalid" className='d-flex justify-content-start align-items-center'>This is not perfect debate topic.
                                                    <Info
                                                        role="button"
                                                        size={16}
                                                        onClick={() => setShowTopicInfo(true)}
                                                    />
                                                </Form.Control.Feedback>
                                            )}
                                        </Form.Group>

                                        <Form.Group>
                                            <div className='d-flex align-items-center justify-content-between'>
                                                <Form.Label className="fw-semibold">Debate Preference</Form.Label>
                                                <div className='d-flex gap-4'>
                                                    <Form.Label className="fw-semibold">Support</Form.Label>
                                                    <Form.Check
                                                        type="switch"
                                                        id="custom-switch"
                                                        label="Against"
                                                        name="argueType"
                                                        checked={formData.argueType}
                                                        onChange={(e) => handleChange("argueType", e.target.checked)}
                                                    />
                                                </div>
                                            </div>
                                        </Form.Group>

                                        {/* <div>
                                        </div> */}
                                        {/* <div className='d-flex gap-4'> */}
                                        {/* <Form.Label className="fw-semibold">With</Form.Label> */}
                                        {/* <Form.Check
                                                type="switch"
                                                id="custom-switch"
                                                label="Against"
                                                name="argueType"
                                                checked={formData.argueType}
                                                onChange={(e) => handleChange("argueType", e.target.checked)}
                                            /> */}
                                        {/* <Form.Control
                                                name="against"
                                                value={formData.against}
                                                onChange={(e) => handleChange("against", e.target.value)}
                                                placeholder="Humans / Ethics / AI Skeptic"
                                                required
                                            /> */}
                                        {/* </div> */}

                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Difficulty Level</Form.Label>
                                            <div className="d-flex justify-content-between mt-2">
                                                {["easy", "medium", "hard"].map((level) => (
                                                    <Form.Check
                                                        key={level}
                                                        type="radio"
                                                        label={level.charAt(0).toUpperCase() + level.slice(1)}
                                                        name="difficulty"
                                                        value={level}
                                                        checked={formData.difficulty === level}
                                                        onChange={() => handleChange("difficulty", level)}
                                                    />
                                                ))}
                                            </div>
                                        </Form.Group>

                                        <div className="d-flex align-items-center justify-content-between">
                                            <Form.Label className="fw-semibold mb-0">Select Minutes</Form.Label>

                                            <ButtonGroup>
                                                {["5", "10", "15"].map((time) => (
                                                    <Button
                                                        key={time}
                                                        variant={formData.duration === time ? "primary" : "outline-primary"}
                                                        onClick={() => handleChange("duration", time)}
                                                    >
                                                        {time} min
                                                    </Button>
                                                ))}
                                            </ButtonGroup>
                                        </div>

                                        <Button
                                            variant="primary"
                                            size="lg"
                                            className="w-100 py-2 fw-bold shadow-sm"
                                            type='submit'
                                        >
                                            {loader ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Initializing Session...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-play-circle-fill me-2"></i>Start Practice
                                                </>
                                            )}
                                        </Button>
                                    </Form>
                                    <div className='text-end mt-4 mb-3'>
                                        <Button
                                            variant="link"
                                            className="p-0 text-decoration-none small fw-semibold"
                                            onClick={() => setShowHowItWorks(true)}
                                        >
                                            How it works?
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Modal
                show={showHowItWorks}
                onHide={() => setShowHowItWorks(false)}
                centered
                size="lg"
            >
                <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
                    <Modal.Title className="fw-bold">
                        <i className="bi bi-info-circle-fill text-primary me-2"></i>
                        How It Works
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4 pt-3">
                    <Row className="g-4">
                        {howItWorksSteps.map((item, idx) => (
                            <Col md={6} key={idx}>
                                <div
                                    className="h-100 p-3 rounded-3 border bg-light d-flex flex-column gap-2"
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <div
                                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-bold"
                                            style={{ width: '28px', height: '28px', fontSize: '0.8rem' }}
                                        >
                                            {item.step}
                                        </div>
                                        <h6 className="fw-bold mb-0">{item.title}</h6>
                                    </div>
                                    <p className="text-muted small mb-0">{item.desc}</p>
                                </div>
                            </Col>
                        ))}
                    </Row>
                    <div className="mt-4 text-center">
                        <Button variant="primary" onClick={() => setShowHowItWorks(false)} className="px-4 fw-semibold">
                            Got it, let's start!
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal
                show={showTopicInfo}
                onHide={() => setShowTopicInfo(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>How to Choose a Perfect Debate Topic</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <h6>✅ Recommended (Best Experience)</h6>
                    <p>Use <strong>statement-based topics</strong> that express a clear opinion.</p>
                    <ul>
                        <li>AI will replace most human jobs.</li>
                        <li>Space exploration is not worth the cost.</li>
                        <li>Social media does more harm than good.</li>
                    </ul>

                    <h6 className="mt-3">⚠️ Comparative Topics</h6>
                    <p>Topics with <strong>vs / versus</strong> are supported, but you must select a side.</p>
                    <ul>
                        <li>AI vs Human Intelligence</li>
                        <li>Online vs Offline Education</li>
                    </ul>

                    <h6 className="mt-3">❌ Avoid</h6>
                    <ul>
                        <li>One-word topics (e.g., AI, Technology)</li>
                        <li>Questions (e.g., Is AI good or bad?)</li>
                        <li>Vague descriptions</li>
                    </ul>
                </Modal.Body>
            </Modal>

        </>
    );
}

export default DebatePrep;